/**
 * FileArtefactViewer — renders download + preview buttons for generated document files.
 *
 * The artefact content is a JSON string parsed via `parseFileArtefact` into
 * { filename, file_size_bytes, content_base64 }.
 *
 * HTML/SVG are previewed inside a sandboxed iframe (`allow-scripts`, no
 * `allow-same-origin`) carrying a restrictive CSP, so skill/LLM-generated markup
 * cannot reach the app's origin, its storage/tokens, or the network. SVG is
 * additionally DOMPurify-sanitised. This replaces opening a same-origin `blob:`
 * URL in a new tab, which would have executed embedded scripts in the app origin.
 */

import type { Artefact, FileArtefactPayload } from "@/types/artefacts";

import React, { useCallback, useMemo, useState } from "react";
import { Button, Chip, toast } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import { ModalOverlay, ModalHeader, ModalBody } from "@/components/UI/ModalOverlay";
import { FilePreviewModal } from "@/components/FilePreview/FilePreviewModal";
import { formatFileSize } from "@/utils/formatFileSize";
import { parseFileArtefact } from "@/types/artefacts";
import { logger } from "@/telemetry";

interface FileArtefactViewerProps {
  artefact: Artefact;
}

// Maximum decoded size we will hold in memory. Guards against OOM from an
// oversized generated file (CLAUDE.md: check size before loading into memory).
const MAX_FILE_BYTES = 50 * 1024 * 1024;

// CSP injected into the sandboxed preview document. Inline styles/scripts and
// data/blob images are allowed so an interactive design still renders, but all
// network access is blocked so nothing can be exfiltrated even if a script runs.
const PREVIEW_CSP =
  "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; img-src data: blob:; media-src data: blob:; style-src 'unsafe-inline'; font-src data:; script-src 'unsafe-inline';\">";

const FILE_TYPE_ICONS: Record<string, string> = {
  docx: "vscode-icons:file-type-word",
  doc: "vscode-icons:file-type-word",
  pdf: "vscode-icons:file-type-pdf2",
  xlsx: "vscode-icons:file-type-excel",
  xls: "vscode-icons:file-type-excel",
  pptx: "vscode-icons:file-type-powerpoint",
  ppt: "vscode-icons:file-type-powerpoint",
  html: "vscode-icons:file-type-html",
  svg: "vscode-icons:file-type-svg",
};

const FILE_TYPE_MIME: Record<string, string> = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  html: "text/html",
  svg: "image/svg+xml",
};

// Extensions that support in-app preview via FilePreviewModal.
const PREVIEWABLE_EXTENSIONS = new Set(["docx", "pdf", "xlsx"]);
// Extensions previewed inline in a sandboxed iframe (markup that renders in the browser).
const INLINE_PREVIEW_EXTENSIONS = new Set(["html", "htm", "svg"]);

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

/**
 * Sanitise SVG markup before it is rendered. An SVG can embed `<script>`
 * elements and event-handler attributes; DOMPurify's SVG profile strips active
 * content while preserving the vector markup.
 */
function sanitiseSvg(svg: string): string {
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
  });
}

/**
 * Wrap preview content in a network-locked document. For a full HTML file the
 * CSP meta is injected into the existing <head> (falling back to prepending
 * one); SVG (already sanitised) is wrapped in a minimal document.
 */
function buildPreviewDoc(content: string, extension: string): string {
  if (extension === "svg") {
    return `<!doctype html><html><head><meta charset="utf-8">${PREVIEW_CSP}</head><body style="margin:0">${content}</body></html>`;
  }
  if (/<head[^>]*>/i.test(content)) {
    return content.replace(/<head[^>]*>/i, (match) => `${match}${PREVIEW_CSP}`);
  }
  if (/<html[^>]*>/i.test(content)) {
    return content.replace(/<html[^>]*>/i, (match) => `${match}<head>${PREVIEW_CSP}</head>`);
  }

  return `<!doctype html><html><head>${PREVIEW_CSP}</head><body>${content}</body></html>`;
}

export const FileArtefactViewer: React.FC<FileArtefactViewerProps> = ({ artefact }) => {
  const { t } = useTranslation();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isInlinePreviewOpen, setIsInlinePreviewOpen] = useState(false);

  const fileData: FileArtefactPayload | null = useMemo(
    () => parseFileArtefact(artefact.content),
    [artefact.content],
  );

  const extension = useMemo(() => {
    if (!fileData?.filename) return "";
    const parts = fileData.filename.split(".");

    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  }, [fileData?.filename]);

  const icon = FILE_TYPE_ICONS[extension] || "heroicons:document-arrow-down";
  const canPreview = PREVIEWABLE_EXTENSIONS.has(extension);
  const canPreviewInline = INLINE_PREVIEW_EXTENSIONS.has(extension);

  const isTooLarge = !!fileData && fileData.file_size_bytes > MAX_FILE_BYTES;

  // Decode once and reuse for preview, inline preview and download. Null when
  // there is no payload, it exceeds the size cap, or the base64 is corrupt.
  const decoded = useMemo<ArrayBuffer | null>(() => {
    if (!fileData?.content_base64 || isTooLarge) return null;
    try {
      const buffer = base64ToArrayBuffer(fileData.content_base64);

      return buffer.byteLength > MAX_FILE_BYTES ? null : buffer;
    } catch {
      return null;
    }
  }, [fileData?.content_base64, isTooLarge]);

  const isCorrupt = !!fileData?.content_base64 && !isTooLarge && decoded === null;
  const documentData = canPreview ? decoded : null;

  const inlinePreviewDoc = useMemo(() => {
    if (!canPreviewInline || !decoded) return null;
    try {
      const raw = new TextDecoder().decode(decoded);
      const body = extension === "svg" ? sanitiseSvg(raw) : raw;

      return buildPreviewDoc(body, extension);
    } catch (err) {
      logger.error("Failed to build file preview", { error: String(err) });

      return null;
    }
  }, [canPreviewInline, decoded, extension]);

  const handleDownload = useCallback(() => {
    if (!fileData?.filename) return;

    if (!decoded) {
      toast.danger(t("This file could not be decoded and may be corrupt."));

      return;
    }

    try {
      const mime = FILE_TYPE_MIME[extension] || "application/octet-stream";
      const blob = new Blob([decoded], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("Failed to download file", { error: String(err) });
      toast.danger(t("Download failed. Please try again."));
    }
  }, [fileData, extension, decoded, t]);

  if (!fileData) {
    return (
      <div className="p-4 text-center text-default-400">
        <p>{t("Unable to load file data")}</p>
      </div>
    );
  }

  const errorMessage = isTooLarge
    ? t("This file is too large to preview or download in the browser.")
    : isCorrupt
      ? t("This file could not be decoded and may be corrupt.")
      : null;

  return (
    <>
      <div className="flex flex-col items-center gap-4 p-6">
        <Icon className="text-primary" icon={icon} width={64} />

        <div className="text-center">
          <p className="text-sm font-semibold">{fileData.filename}</p>
          <Chip className="mt-1" size="sm" variant="soft">
            {formatFileSize(fileData.file_size_bytes || 0)}
          </Chip>
        </div>

        {errorMessage ? (
          <p className="max-w-sm text-center text-sm text-danger">{errorMessage}</p>
        ) : (
          <div className="flex gap-2 mt-2">
            {canPreview && (
              <Button variant="outline" onPress={() => setIsPreviewOpen(true)}>
                <Icon icon="heroicons:eye" width={18} />
                {t("Preview")}
              </Button>
            )}
            {canPreviewInline && (
              <Button variant="outline" onPress={() => setIsInlinePreviewOpen(true)}>
                <Icon icon="heroicons:arrow-top-right-on-square" width={18} />
                {t("Preview")}
              </Button>
            )}
            <Button variant="primary" onPress={handleDownload}>
              <Icon icon="heroicons:arrow-down-tray" width={18} />
              {t("Download {{type}}", { type: extension.toUpperCase() })}
            </Button>
          </div>
        )}
      </div>

      {canPreview && (
        <FilePreviewModal
          documentData={documentData}
          fileName={fileData.filename}
          fileType={FILE_TYPE_MIME[extension]}
          isOpen={isPreviewOpen}
          onDownload={handleDownload}
          onOpenChange={setIsPreviewOpen}
        />
      )}

      {canPreviewInline && (
        <ModalOverlay isOpen={isInlinePreviewOpen} size="4xl" onOpenChange={setIsInlinePreviewOpen}>
          <ModalHeader className="border-b border-divider">{fileData.filename}</ModalHeader>
          <ModalBody className="p-0">
            {inlinePreviewDoc ? (
              <iframe
                className="w-full h-[70vh] border-0 bg-white"
                data-testid="file-inline-preview"
                sandbox="allow-scripts"
                srcDoc={inlinePreviewDoc}
                title={fileData.filename}
              />
            ) : (
              <p className="p-6 text-center text-default-400">{t("Unable to load file data")}</p>
            )}
          </ModalBody>
        </ModalOverlay>
      )}
    </>
  );
};
